import { Component, Input, Inject, OnInit, OnChanges, OnDestroy, SimpleChanges, EventEmitter, Output } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';

import { IClassesEntityService, ActiveSessionDto } from '../../../../../../interfaces/entities/classes-entity-service.interface';
import { CLASSES_ENTITY_SERVICE_TOKEN } from '../../../../../../injection-tokens/entity-service-tokens';
import { Log } from '../../../../../../services/logger.service';
import { ConnectionSharingService } from '../../../../../../services/connection-sharing.service';
import { UnifiedVmLauncherService } from '../../../../../../services/unified-vm-launcher.service';

@Component({
  selector: 'app-activity-tab',
  templateUrl: './activity-tab.component.html',
  styleUrls: ['./activity-tab.component.css'],
})
export class ActivityTabComponent implements OnInit, OnChanges, OnDestroy {
  @Input() classId!: string;
  @Input() userRole!: string;
  @Input() userName!: string;
  /** True when the class has a VM resource assigned. Used to distinguish empty states. */
  @Input() hasVmResource = false;
  /** True once the class VM template has been snapshotted (Testing Done). */
  @Input() hasSnapshot = false;
  /**
   * VM Connection Template id currently attached to this class (or null when none).
   * Forwarded into the operator-remote-vm launcher so the encrypted Guacamole token reflects
   * the operator's selection rather than the global appsettings.json defaults.
   */
  @Input() vmConnectionTemplateId: string | null = null;

  /** Emitted by the "Go to Settings → Participants" button in the not-confirmed empty state. */
  @Output() goToSettingsParticipants = new EventEmitter<void>();

  /** Emitted by the "Go to Settings → VM Template  " button in the not-confirmed empty state. */
  @Output() goToSettingsVMTemplate = new EventEmitter<void>();

  sessions: ActiveSessionDto[] = [];
  isLoading = false;
  isRefreshing = false;
  loadError: string | null = null;
  isLaunching = false;
  launchingUserId: string | null = null;

  pendingSession: any = null;
  showJoinConfirm = false;

  private refreshInterval: ReturnType<typeof setInterval> | null = null;
  private readonly REFRESH_INTERVAL_MS = 5000;
  private readonly logger = Log.create(ActivityTabComponent);

  constructor(
    @Inject(CLASSES_ENTITY_SERVICE_TOKEN) private readonly classService: IClassesEntityService,
    private readonly connectionSharingService: ConnectionSharingService,
    private readonly unifiedLauncher: UnifiedVmLauncherService,
    private readonly snackBar: MatSnackBar,
  ) {}

  ngOnInit(): void {
    this.loadActivity();
    this.startAutoRefresh();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['classId'] && !changes['classId'].firstChange) {
      this.stopAutoRefresh();
      this.loadActivity();
      this.startAutoRefresh();
    }
  }

  ngOnDestroy(): void {
    this.stopAutoRefresh();
  }

  loadActivity(silent = false): void {
    if (!this.classId) return;
    this.loadError = null;

    // Silently refresh when sessions already exist or explicitly requested as silent;
    // only replace the full view with a spinner on the initial (empty) load.
    if (silent || this.sessions.length > 0) {
      this.isRefreshing = true;
    } else {
      this.isLoading = true;
    }

    this.classService.getActiveSessionsByClassId(this.classId).subscribe({
      next: (res: ActiveSessionDto[]) => {
        this.sessions = res ?? [];
        this.isLoading = false;
        this.isRefreshing = false;
      },
      error: (err: any) => {
        this.loadError = err?.error?.message ?? 'Failed to load active sessions.';
        this.isLoading = false;
        this.isRefreshing = false;
        this.logger.e('Activity load error:', err);
      },
    });
  }

  // Called by the Refresh button — resets the auto-refresh interval so the next
  // automatic tick is 5 seconds after the manually triggered one.
  onRefreshClick(): void {
    this.stopAutoRefresh();
    this.loadActivity();
    this.startAutoRefresh();
  }

  private startAutoRefresh(): void {
    this.stopAutoRefresh();
    this.refreshInterval = setInterval(() => {
      // Skip if a load or refresh is already in flight.
      if (!this.isLoading && !this.isRefreshing) {
        this.loadActivity(true);
      }
    }, this.REFRESH_INTERVAL_MS);
  }

  private stopAutoRefresh(): void {
    if (this.refreshInterval !== null) {
      clearInterval(this.refreshInterval);
      this.refreshInterval = null;
    }
  }

  getConnectedAt(session: ActiveSessionDto): string {
    return session?.connectedAt ? new Date(session.connectedAt).toLocaleString() : '—';
  }

  getThumbnailAge(session: ActiveSessionDto): string {
    if (!session?.thumbnailUpdatedAt) return '';
    const diffMs = Date.now() - new Date(session.thumbnailUpdatedAt).getTime();
    const diffMin = Math.floor(diffMs / 60_000);
    if (diffMin < 1) return 'Just now';
    if (diffMin < 60) return `${diffMin} min ago`;
    return `${Math.floor(diffMin / 60)} hr ago`;
  }

  onJoinClick(session: any): void {
    this.pendingSession = session;
    this.showJoinConfirm = true;
  }

  cancelJoin(): void {
    this.showJoinConfirm = false;
    this.pendingSession = null;
  }

  confirmJoin(): void {
    const session = this.pendingSession;
    this.showJoinConfirm = false;
    this.pendingSession = null;
    if (!session) return;

    this.isLaunching = true;
    this.launchingUserId = session.userId;
    const participantUserId = session.userId;
    const trainerUserId = localStorage.getItem('userId') ?? '';
    const vmId = session.publicIp ?? session.publicIP ?? session.vmId ?? session.hostname;

    if (!vmId) {
      this.isLaunching = false;
      this.launchingUserId = null;
      this.notify('Target VM public IP is missing for this session', 'error');
      return;
    }

    const preOpenedWindow = window.open('', `vm_${Date.now()}`);

    this.unifiedLauncher.launchOperatorRemoteVm({
      operatorUserId: trainerUserId,
      classId: this.classId,
      studentUserId: participantUserId,
      vmId,
      userName: this.userName,
      vmConnectionTemplateId: this.vmConnectionTemplateId,
    }, preOpenedWindow).then(() => {
      this.isLaunching = false;
      this.launchingUserId = null;
      this.notify(`Joined ${session.userName ?? 'participant'}'s session`, 'success');
    }).catch((err: any) => {
      this.isLaunching = false;
      this.launchingUserId = null;
      preOpenedWindow?.close();
      this.logger.e('Join session error:', err);
      const msg = err?.error?.message ?? err?.message ?? 'Failed to join session';
      this.notify(msg.includes('No active connection') ? `${session.userName ?? 'Participant'} is not currently connected` : msg, 'error');
    });
  }

  private notify(msg: string, type: 'success' | 'error'): void {
    this.snackBar.open(msg, 'Dismiss', {
      duration: 3000,
      panelClass: type === 'success' ? 'snack-success' : 'snack-error',
    });
  }
}
